<template>
  <section class="test-page">
    <!-- HERO / HEADER IMAGE -->
    <div class="hero-wrap">
      <div class="hero-placeholder" role="img" aria-label="Hero image placeholder"></div>

      <div class="hero-overlay container-fluid">
        <div class="hero-title">
          <div class="hero-title-line">Climate Community</div>
          <div class="hero-title-line hero-title-line--big">Sustainability</div>
          <div class="hero-title-line hero-title-line--big">Courses</div>
        </div>
     </div>
    </div>

    <!-- COURSE TILES -->
    <div class="px-0 tiles-wrap container-fluid">
      <div
        class="tiles-slider"
        aria-label="Course slider"
        @mouseenter="onHoverStart"
        @mouseleave="onHoverEnd"
        @focusin="onHoverStart"
        @focusout="onHoverEnd"
        @pointerdown="onPointerDown"
        @pointermove="onPointerMove"
        @pointerup="onPointerUp"
        @pointercancel="onPointerUp"
      >
        <div v-if="loading" class="slider-status">Loading courses…</div>
        <div v-else-if="error" class="slider-status slider-status--error">{{ error }}</div>
        <div v-else-if="courses.length === 0" class="slider-status">No courses found.</div>

        <button
          type="button"
          class="slider-arrow slider-arrow--left"
          aria-label="Previous courses"
          @click="prevPage"
        >
          ‹
        </button>

        <button
          type="button"
          class="slider-arrow slider-arrow--right"
          aria-label="Next courses"
          @click="nextPage"
        >
          ›
        </button>

        <div
          ref="tilesTrack"
          class="tiles-track"
          :style="{ transform: `translateX(-${offsetPx}px)` }"
        >
          <div v-for="(page, pageIndex) in loopPages" :key="pageIndex" class="tiles-page">
            <RouterLink
              v-for="(course, courseIndex) in page"
              :key="`${pageIndex}-${courseIndex}-${course.id ?? 'course'}`"
              :to="courseDetailsRoute(course)"
              custom
              v-slot="{ href, navigate }"
            >
              <a
                :href="href"
                class="tile tile--clickable tile-link"
                @click="navigate"
                @pointerdown.stop
              >
                <div class="tile-img">
                  <img
                    v-if="course.imageUrl"
                    class="tile-img-el"
                    :src="course.imageUrl"
                    :alt="course.title"
                    loading="lazy"
                  />
                  <div v-else class="img-placeholder" role="img" :aria-label="`${course.title} image placeholder`"></div>
                </div>
                <div class="tile-caption">{{ course.title }}</div>
              </a>
            </RouterLink>
          </div>
        </div>
      </div>
    </div>

    <!-- BOTTOM BANNER -->
    <div class="banner-wrap">
      <div class="banner-placeholder" role="img" aria-label="Banner image placeholder"></div>
      <div class="banner-caption">Theology, Culture &amp; Climate Justice</div>
    </div>
  </section>
</template>

<script>
import { apiFetch, API_BASE_URL } from '../../api/client'

export default {
  name: 'ClimateCommunitySustainability',
  data() {
    return {
      loading: true,
      error: null,
      courses: [],
      categoryId: 13,

      // Slider behavior
      perPage: 5,
      secondsPerPage: 60,
      paused: false,
      hovering: false,
      dragging: false,
      dragStartX: 0,
      dragStartOffset: 0,
      didDrag: false,
      offsetPx: 0,
      pageWidthPx: 0,
      rafId: null,
      lastTs: 0,
      resizeObserver: null,
    }
  },
  computed: {
    sliderCourses() {
      // If there aren't enough courses to fill the 5-tile row (or to create
      // multiple pages), repeat courses so the slider stays continuous and
      // never shows empty whitespace.
      const base = Array.isArray(this.courses) ? this.courses : []
      if (base.length === 0) return []

      // We want at least 2 full pages so `shouldLoop` stays true.
      const minTiles = Math.max(this.perPage, this.perPage * 2)

      const repeated = []
      // Keep adding until we have enough tiles AND the total is divisible
      // by perPage, so every page is fully populated.
      while (repeated.length < minTiles || (repeated.length % this.perPage) !== 0) {
        repeated.push(...base)
      }

      return repeated
    },
    coursePages() {
      const pages = []
      for (let i = 0; i < this.sliderCourses.length; i += this.perPage) {
        pages.push(this.sliderCourses.slice(i, i + this.perPage))
      }
      return pages
    },
    shouldLoop() {
      return this.coursePages.length > 1
    },
    loopPages() {
      return this.shouldLoop ? this.coursePages.concat(this.coursePages) : this.coursePages
    },
    loopWidthPx() {
      return this.pageWidthPx * this.coursePages.length
    }
  },
  methods: {
    courseTitle(course) {
      return (
        course.course_name ||
        course.course_title ||
        course.title ||
        course.name ||
        'Course'
      )
    },
    courseImageUrl(pictureUrl) {
      if (!pictureUrl) return ''

      // Already absolute
      if (/^https?:\/\//i.test(pictureUrl) || /^\/\//.test(pictureUrl)) {
        return pictureUrl
      }

      // Ensure relative paths like "upload/..." resolve against the backend origin
      const base = (API_BASE_URL || '').replace(/\/$/, '')
      const path = String(pictureUrl).replace(/^\/+/, '')
      return base ? `${base}/${path}` : `/${path}`
    },

    // Slider controls
    pause() {
      this.paused = true
    },
    resume() {
      this.paused = false
    },
    onHoverStart() {
      this.hovering = true
      this.pause()
    },
    onHoverEnd() {
      this.hovering = false
      if (!this.dragging) this.resume()
    },
    normalizeOffset() {
      if (this.shouldLoop && this.loopWidthPx > 0) {
        this.offsetPx = ((this.offsetPx % this.loopWidthPx) + this.loopWidthPx) % this.loopWidthPx
      } else {
        this.offsetPx = 0
      }
    },
    updateMeasurements() {
      const trackEl = this.$refs.tilesTrack
      const sliderEl = trackEl ? trackEl.parentElement : null
      if (!sliderEl) return

      const nextWidth = sliderEl.clientWidth
      if (!nextWidth) return

      if (this.pageWidthPx && this.loopWidthPx) {
        const ratio = this.offsetPx / this.loopWidthPx
        this.pageWidthPx = nextWidth
        this.offsetPx = ratio * this.loopWidthPx
      } else {
        this.pageWidthPx = nextWidth
      }

      if (this.shouldLoop && this.loopWidthPx) {
        this.normalizeOffset()
      } else {
        this.offsetPx = 0
      }
    },
    startLoop() {
      if (this.rafId) return
      this.lastTs = 0
      this.rafId = window.requestAnimationFrame(this.tick)
    },
    stopLoop() {
      if (this.rafId) {
        window.cancelAnimationFrame(this.rafId)
        this.rafId = null
      }
      this.lastTs = 0
    },
    tick(ts) {
      if (!this.lastTs) this.lastTs = ts
      const deltaMs = ts - this.lastTs
      this.lastTs = ts

      if (!this.paused && this.shouldLoop && this.loopWidthPx > 0 && this.pageWidthPx > 0) {
        const speedPxPerMs = (this.pageWidthPx / this.secondsPerPage) / 1000
        this.offsetPx += deltaMs * speedPxPerMs
        if (this.offsetPx >= this.loopWidthPx) {
          this.offsetPx -= this.loopWidthPx
        }
      }

      this.rafId = window.requestAnimationFrame(this.tick)
    },
    onPointerDown(e) {
      if (e.pointerType === 'mouse' && e.button !== 0) return
      if (!this.shouldLoop || !this.loopWidthPx) return

      this.dragging = true
      this.didDrag = false
      this.dragStartX = e.clientX
      this.dragStartOffset = this.offsetPx
      this.pause()

      try {
        e.currentTarget.setPointerCapture(e.pointerId)
      } catch (_) {
        // ignore
      }
    },
    onPointerMove(e) {
      if (!this.dragging) return
      const dx = e.clientX - this.dragStartX
      if (Math.abs(dx) > 4) this.didDrag = true

      this.offsetPx = this.dragStartOffset - dx
      this.normalizeOffset()

      if (this.didDrag) e.preventDefault()
    },
    onPointerUp(e) {
      if (!this.dragging) return
      this.dragging = false

      try {
        e.currentTarget.releasePointerCapture(e.pointerId)
      } catch (_) {
        // ignore
      }

      if (this.pageWidthPx > 0 && this.loopWidthPx > 0) {
        const pageIndex = Math.round(this.offsetPx / this.pageWidthPx) % this.coursePages.length
        this.offsetPx = pageIndex * this.pageWidthPx
        this.normalizeOffset()
      }

      if (!this.hovering) this.resume()
    },
    nextPage() {
      if (!this.shouldLoop || !this.loopWidthPx) return
      this.offsetPx = (this.offsetPx + this.pageWidthPx) % this.loopWidthPx
    },
    prevPage() {
      if (!this.shouldLoop || !this.loopWidthPx) return
      const next = this.offsetPx - this.pageWidthPx
      this.offsetPx = next < 0 ? (this.loopWidthPx + next) : next
    },
    courseDetailsRoute(course) {
      return {
        name: 'course-details',
        query: course?.title ? { title: course.title } : undefined,
      }
    },
  },
  async mounted() {
    try {
      const res = await apiFetch(`/api/dept-courses?categoryId=${encodeURIComponent(this.categoryId)}`)
      const json = await res.json()
      if (json.status !== 'ok') {
        throw new Error(json.message || 'Failed to load courses')
      }

      const rawCourses = Array.isArray(json.courses) ? json.courses : []
      this.courses = rawCourses.map((c) => {
        const title = this.courseTitle(c)
        const imageUrl = c.picture_url ? this.courseImageUrl(c.picture_url) : ''
        return { id: c.id, title, imageUrl }
      })
    } catch (e) {
      this.error = e.message
    } finally {
      this.loading = false
    }

    // Start slider loop after initial render.
    this.$nextTick(() => {
      this.updateMeasurements()

      if (window.ResizeObserver) {
        this.resizeObserver = new window.ResizeObserver(() => this.updateMeasurements())
        if (this.$refs.tilesTrack && this.$refs.tilesTrack.parentElement) {
          this.resizeObserver.observe(this.$refs.tilesTrack.parentElement)
        }
      } else {
        window.addEventListener('resize', this.updateMeasurements)
      }

      this.startLoop()
    })
  },
  beforeUnmount() {
    this.stopLoop()
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
      this.resizeObserver = null
    } else {
      window.removeEventListener('resize', this.updateMeasurements)
    }
  }
};
</script>

<style scoped>
.test-page {
  width: 100%;
}

/* Shared poster colors */
:root {
  --poster-maroon: #971836;
  --poster-maroon-dark: #74112a;
}

/* HERO */
.hero-wrap {
  position: relative;
  width: 100%;
  height: 460px;
  overflow: hidden;
}

.hero-placeholder {
  position: absolute;
  inset: 0;
   background: url('../../../public/assets/img/climate-finance-composition2.jpg') center/cover no-repeat;

}

.hero-overlay {
  position: relative;
  z-index: 1;
  height: 100%;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding: 26px 18px;
  text-align: center;
}

.hero-title {
  width: 100%;
  max-width: 900px;
  margin-left: auto;
  margin-right: auto;
  line-height: 0.95;
}

.hero-title-line {
  font-weight: 800;
  color: #fff;
  font-size: 40px;
  letter-spacing: 0.5px;
  text-transform: none;
  text-shadow:
    -2px -2px 0 rgba(0, 0, 0, 0.7),
     2px -2px 0 rgba(0, 0, 0, 0.7),
    -2px  2px 0 rgba(0, 0, 0, 0.7),
     2px  2px 0 rgba(0, 0, 0, 0.7),
     0 10px 28px rgba(0, 0, 0, 0.45);
}

.hero-title-line--big {
  font-size: 56px;
}

/* TILES */
.tiles-wrap {
  background: #ffffff00;
  --tile-border-color: #0E2447;
  --tile-border-width: 5px;
}

.tiles-slider {
  position: relative;
  overflow: hidden;
  border-left: var(--tile-border-width) solid var(--tile-border-color);
  border-right: var(--tile-border-width) solid var(--tile-border-color);
  border-bottom: 3px solid var(--tile-border-color);
  border-top: var(--tile-border-width) solid var(--tile-border-color);
  cursor: grab;
  touch-action: pan-y;
}

.tiles-slider:active {
  cursor: grabbing;
}

.slider-status {
  position: absolute;
  inset: 0;
  z-index: 3;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 16px;
  color: #fff;
  background: rgba(0, 0, 0, 0.25);
  font-weight: 700;
}

.slider-status--error {
  background: rgba(151, 24, 54, 0.45);
}

.slider-arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 4;
  width: 44px;
  height: 44px;
  border-radius: 999px;
  border: 2px solid rgba(255, 255, 255, 0.75);
  background: rgba(0, 0, 0, 0.35);
  color: #fff;
  font-size: 28px;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.slider-arrow--left { left: 10px; }
.slider-arrow--right { right: 10px; }

.slider-arrow:hover,
.slider-arrow:focus-visible {
  background: rgba(0, 0, 0, 0.55);
}

.tiles-track {
  display: flex;
  width: 100%;
  transition: none;
  will-change: transform;
}

@media (prefers-reduced-motion: reduce) {
  .slider-arrow { display: none; }
}

.tiles-page {
  flex: 0 0 100%;
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 0;
}

.tile {
  position: relative;
  border-right: var(--tile-border-width) solid var(--tile-border-color);
  background: #971D39;
}

.tile--clickable {
  cursor: pointer;
}

.tile-link {
  display: block;
  color: inherit;
  text-decoration: none;
}

.tiles-page .tile:nth-child(5n) {
  border-right: none;
}

.tiles-page + .tiles-page .tile:first-child {
  border-left: var(--tile-border-width) solid var(--tile-border-color);
}

.tile-img {
  height: 480px;
}

.tile-img-el {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.img-placeholder {
  width: 100%;
  height: 100%;
  background:
    radial-gradient(circle at 30% 20%, rgba(255, 255, 255, 0.22), rgba(255, 255, 255, 0) 55%),
    linear-gradient(135deg, #1b2b36, #2f3a45);
}

.tile-caption {
  background: var(--poster-maroon);
  border-top: 3px solid var(--poster-maroon-dark);
  color: #fff;
  font-weight: 700;
  text-align: center;
  padding: 10px 10px;
  font-size: 12px;
  line-height: 1.1;
  min-height: 46px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* BANNER */
.banner-wrap {
  position: relative;
  width: 100%;
  height: 300px;
  overflow: hidden;
  border-left: 5px solid #0E2447;
  border-right: 5px solid #0E2447;
  border-bottom: 5px solid #0E2447;
}

.banner-placeholder {
  position: absolute;
  inset: 0;
  background: url('../../../public/assets/img/climate-finance-composition46.jpg') center/cover no-repeat;
    
}

.banner-caption {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 10px 14px;
  background: #971D39;
  border-top: 3px solid var(--poster-maroon-dark);
  color: #fff;
  font-weight: 800;
  text-align: center;
  letter-spacing: 0.3px;
}

@media (max-width: 767.98px) {
  .hero-wrap { height: 300px; }
  .hero-title-line { font-size: 30px; }
  .hero-title-line--big { font-size: 42px; }
  .tile-img { height: 360px; }
}
</style>
